import { NavLink } from 'react-router-dom';
import { FiCheck } from 'react-icons/fi';
import ProofStats from '../../components/ProofStats';
import CountUp from '../../components/CountUp';
import useInView from '../../hooks/useInView';

const HERO_METRICS = [
  { value: '1.4M+', label: 'Tracked entities' },
  { value: '120+', label: 'Industry datasets' },
  { value: '24/7', label: 'Signal monitoring' },
];

const PROOF = [
  { value: '98%', label: 'Data quality SLAs', visual: { type: 'ring', pct: 98 } },
  {
    value: '4 hrs',
    label: 'Avg. report turnaround',
    note: 'Clients tell us cycles went from days to hours.',
    visual: {
      type: 'compare',
      rows: [
        { label: 'Before: days', size: 1 },
        { label: 'Trelledex: 4 hrs', size: 0.14, ours: true },
      ],
    },
  },
  { value: '40+', label: 'Partner workflows', visual: { type: 'dots', count: 40 } },
];

const WORKFLOW = [
  { title: 'Discovery', text: 'We map the signal sources and define the KPIs that matter to your team.' },
  { title: 'Modeling', text: 'Our analysts normalize data and build consistent classification layers.' },
  { title: 'Automation', text: 'Reporting pipelines sync to your dashboards and deliver weekly updates.' },
  { title: 'Activation', text: 'Stakeholders receive clear insights with context, benchmarks, and alerts.' },
];

const SKILLS = [
  { name: 'Data Analysis & Statistics', level: 92 },
  { name: 'Data Visualization', level: 88 },
  { name: 'Design', level: 84 },
  { name: 'Strategy', level: 86 },
  { name: 'Business Intelligence', level: 90 },
  { name: 'Project Management', level: 82 },
];

const Home = ({ onEnter }) => {
  const [metricsRef, metricsIn] = useInView({ threshold: 0.5 });
  const [skillsRef, skillsIn] = useInView({ threshold: 0.3 });

  return (
    <div className="public-page">
      <section className="hero-split reveal">
        <div>
          <p className="eyebrow">Trelledex Intelligence Platform</p>
          <h1>Data-grade analytics and reporting for confident decisions.</h1>
          <p className="subtitle">
            Trelledex delivers data services, reporting, and end-to-end software
            implementations for teams and businesses that demand clarity.
          </p>
          <div className="hero-actions">
            <button className="btn btn-primary" onClick={onEnter}>
              Enter Platform
            </button>
          </div>
          <div className="hero-metrics" ref={metricsRef}>
            {HERO_METRICS.map((m) => (
              <div key={m.label}>
                <CountUp className="metric-value" value={m.value} start={metricsIn} />
                <p className="metric-label">{m.label}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="hero-side">
          <div className="hero-card">
            <h3>What you get</h3>
            <ul className="hero-checks">
              <li><FiCheck aria-hidden="true" />Curated deals and data signals</li>
              <li><FiCheck aria-hidden="true" />Industry benchmarks and forecasts</li>
              <li><FiCheck aria-hidden="true" />Export-ready reporting packs</li>
              <li><FiCheck aria-hidden="true" />Secure access for teams and partners</li>
            </ul>
            <div className="hero-card-footer">
              <span className="badge">Enterprise-ready</span>
              <span className="badge">Weekly refresh</span>
            </div>
          </div>
          <div className="card narrative-card">
            <h3>Designed for analysts</h3>
            <p className="subtitle">
              Standardized data models, source lineage, and export-ready
              structures help teams deliver clear reports fast.
            </p>
          </div>
        </div>
      </section>

      <section className="feature-grid reveal">
        <div className="card feature-card">
          <h3>Data</h3>
          <p className="subtitle">
            Structured industry and company datasets delivered clean and actionable.
          </p>
        </div>
        <div className="card feature-card">
          <h3>Reporting</h3>
          <p className="subtitle">
            Professional dashboards and exports tailored for executive briefings.
          </p>
        </div>
        <div className="card feature-card">
          <h3>Software Implementations</h3>
          <p className="subtitle">
            End-to-end software implementation services — from scoping and
            architecture to delivery and support.
          </p>
        </div>
        <div className="card feature-card">
          <h3>Advisory</h3>
          <p className="subtitle">
            Partner-ready insights, competitive intelligence, and deal context for
            every sector we cover.
          </p>
        </div>
      </section>

      <ProofStats stats={PROOF}>
        <div className="stats-action">
          <NavLink to="/contact" className="btn btn-primary">
            Talk to Trelledex
          </NavLink>
          <NavLink to="/products" className="btn btn-outline">
            View offerings
          </NavLink>
        </div>
      </ProofStats>

      <section className="card narrative-card reveal">
        <h3>Core Solutions</h3>
        <p className="subtitle">
          Data analytics, business intelligence, and software delivery designed
          to improve reporting accuracy and reduce turnaround time.
        </p>
      </section>

      <section className="workflow-grid reveal">
        {WORKFLOW.map((step, i) => (
          <div key={step.title} className="card workflow-card">
            <p className="eyebrow">0{i + 1}</p>
            <h3>{step.title}</h3>
            <p className="subtitle">{step.text}</p>
          </div>
        ))}
      </section>

      <section className="innovation-panel reveal">
        <div>
          <p className="eyebrow">Software delivery</p>
          <h3>Built by engineers who ship.</h3>
          <p className="subtitle">
            Our product studio prototypes dashboards, automation, and AI-assisted
            reporting pipelines to help partners move faster.
          </p>
          <div className="hero-actions">
            <NavLink to="/core-solutions" className="btn btn-primary">
              Explore solutions
            </NavLink>
            <NavLink to="/contact" className="btn btn-outline">
              Book a consult
            </NavLink>
          </div>
        </div>
        <div className="code-card">
          <p className="code-line">$ insights.generate()</p>
          <p className="code-line">$ workflows.sync()</p>
          <p className="code-line">$ reports.publish()</p>
          <p className="code-line accent">$ partners.invite()</p>
        </div>
      </section>

      <section ref={skillsRef} className="skills-section reveal">
        <div className="skills-intro">
          <p className="eyebrow">What we're good at</p>
          <h3>Our main skills</h3>
          <p className="subtitle">
            Every solution we deliver blends deep research, data science, and
            hands-on engineering to meet the realities of your organization.
          </p>
          <p className="subtitle">
            We don’t just crunch numbers. We design intelligence systems that
            help partners make confident, timely decisions.
          </p>
        </div>
        <ul className={`skills-list${skillsIn ? ' is-in' : ''}`}>
          {SKILLS.map((skill, i) => (
            <li key={skill.name} className="skill-item" style={{ '--i': i, '--w': `${skill.level}%` }}>
              <div className="skill-head">
                <strong>{skill.name}</strong>
                <CountUp className="skill-level" value={`${skill.level}%`} start={skillsIn} duration={1200 + i * 100} />
              </div>
              <div className="skill-bar">
                <span />
              </div>
            </li>
          ))}
        </ul>
      </section>

      <section className="testimonial-grid reveal">
        <div className="card testimonial-card">
          <p className="subtitle">
            “Trelledex helped our team cut reporting cycles from days to hours while
            improving confidence in the numbers.”
          </p>
          <p className="testimonial-name">— Head of Strategy, Private Sector</p>
        </div>
        <div className="card testimonial-card">
          <p className="subtitle">
            “The datasets feel curated for decision-makers. We trust the sourcing and
            update cadence.”
          </p>
          <p className="testimonial-name">— Investment Partner</p>
        </div>
        <div className="card testimonial-card">
          <p className="subtitle">
            “Their software team delivered the exact workflow our analysts needed.”
          </p>
          <p className="testimonial-name">— Director of Research</p>
        </div>
      </section>

      <section className="card narrative-card reveal">
        <h3>Why Trelledex</h3>
        <p className="subtitle">
          We prioritize clarity, credibility, and speed. Every dataset is
          verified, every insight is traceable, and every report is built for
          decision-makers.
        </p>
      </section>
    </div>
  );
};

export default Home;
